// #549: „An Kollegen senden“ — eine vorhandene Unterlage als Verweis in den Team-Chat schicken.
// Es wird nichts kopiert: die Chat-Nachricht trägt nur Quelle + ID, geöffnet wird beim Empfänger
// mit dessen Rechten. Die Karte verschwindet mit der wöchentlichen Chat-Leerung, die Datei bleibt.
import { useMemo, useState } from 'react';
import { MagnifyingGlass, PaperPlaneTilt, UserCircle, UsersThree } from '@phosphor-icons/react';
import { DialogHuelle, ImmoButton } from '../immobilien/ui';
import { useChat } from '../../contexts/ChatContext';
import { gruppenName, kollegeName, type ChatAnhang } from '../../lib/chat/nachrichten';

type Ziel = { art: 'person' | 'gruppe'; id: string; name: string };

export function AnKollegenSendenDialog({ anhang, onSchliessen, onFertig }: {
  anhang: ChatAnhang;
  onSchliessen: () => void;
  onFertig: (meldung: string) => void;
}) {
  const { kollegen, gruppen, senden } = useChat();
  const [suche, setSuche] = useState('');
  const [ziel, setZiel] = useState<Ziel | null>(null);
  const [notiz, setNotiz] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const ziele = useMemo<Ziel[]>(() => {
    const alle: Ziel[] = [
      ...gruppen.map((g) => ({ art: 'gruppe' as const, id: g.id, name: gruppenName(g) })),
      ...kollegen.map((k) => ({ art: 'person' as const, id: k.id, name: kollegeName(k) })),
    ];
    const q = suche.trim().toLowerCase();
    return q ? alle.filter((z) => z.name.toLowerCase().includes(q)) : alle;
  }, [kollegen, gruppen, suche]);

  const abschicken = async () => {
    if (!ziel) return;
    setLaeuft(true); setFehler(null);
    const ok = await senden({ art: ziel.art, id: ziel.id }, notiz, null, anhang);
    setLaeuft(false);
    if (!ok) { setFehler('Senden fehlgeschlagen — bitte erneut versuchen.'); return; }
    onFertig(`„${anhang.name}“ an ${ziel.name} gesendet`);
  };

  return (
    <DialogHuelle titel="An Kollegen senden" untertitel={anhang.name} icon={PaperPlaneTilt} onSchliessen={onSchliessen} laeuft={laeuft} breite={460}
      fuss={<>
        <ImmoButton art="fuellung" groesse={44} pille={false} onClick={onSchliessen} disabled={laeuft} style={{ flex: 1 }}>Abbrechen</ImmoButton>
        <ImmoButton art="akzent" groesse={44} pille={false} icon={PaperPlaneTilt} onClick={() => void abschicken()} disabled={laeuft || !ziel} style={{ flex: 1 }}>
          {laeuft ? 'Wird gesendet …' : 'Senden'}
        </ImmoButton>
      </>}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink3)', lineHeight: 1.45 }}>
        Die Unterlage erscheint im Team-Chat als Karte zum Öffnen. Es wird nur darauf verwiesen, nichts kopiert.
      </p>
      <div className="flex items-center" style={{ gap: 8, height: 44, padding: '0 12px', borderRadius: 14, background: 'var(--fill2)' }}>
        <MagnifyingGlass size={15} color="var(--ink3)" />
        <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Kollegin, Kollege oder Team suchen …" aria-label="Empfänger suchen" autoFocus
          className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit' }} />
      </div>
      <div className="immo-scroll flex flex-col overflow-y-auto" role="listbox" aria-label="Empfänger" style={{ maxHeight: 240, gap: 2 }}>
        {ziele.length === 0 && <span style={{ padding: '8px 4px', fontSize: 13, color: 'var(--ink3)' }}>Niemand gefunden</span>}
        {ziele.map((z) => {
          const an = ziel?.art === z.art && ziel.id === z.id;
          const I = z.art === 'gruppe' ? UsersThree : UserCircle;
          return (
            <button key={`${z.art}-${z.id}`} type="button" role="option" aria-selected={an} onClick={() => setZiel(z)}
              className="immo-hover-flaeche flex items-center text-left" style={{ minHeight: 44, gap: 10, padding: '0 10px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                background: an ? 'var(--acc-soft)' : 'transparent', color: an ? 'var(--acc)' : 'var(--ink)', fontSize: 13.5, fontWeight: an ? 800 : 600 }}>
              <I size={18} weight={an ? 'fill' : 'regular'} />
              <span className="min-w-0 flex-1 truncate">{z.name}</span>
              {z.art === 'gruppe' && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>Gruppe</span>}
            </button>
          );
        })}
      </div>
      <textarea value={notiz} onChange={(e) => setNotiz(e.target.value)} rows={2} placeholder="Kurze Notiz (optional)" aria-label="Notiz"
        style={{ width: '100%', padding: '10px 12px', borderRadius: 14, border: 'none', outline: 'none', resize: 'vertical', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit' }} />
      {fehler && <span role="alert" style={{ fontSize: 12.5, color: 'var(--bad)' }}>{fehler}</span>}
    </DialogHuelle>
  );
}
